import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { directory, isForbidden, schedule } from '@cwos/api-client';
import type { schedule as SCH } from '@cwos/api-client';
import { Pill, SkeletonSet, SuccessEmpty } from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { listPhase } from '../listState.js';
import { pickerState } from '../pickerState.js';
import { LoadFailure } from '../LoadFailure.js';
import { SettingsCard, SettingsField } from './common.js';
import {
  DATED_STANDING_READING,
  authorizationStanding,
  dateWindowReading,
  localDateIn,
  messageOf,
} from '../schedule/coverageLogic.js';
import { clientNameResolver } from '../visits/visitLogic.js';
import { authorityGate } from '../authorityGate.js';

/**
 * THE AUTHORIZATION CEILING — what a payer said may not be exceeded.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS SITS BESIDE TARGETS AND NOT ON THE SCHEDULE
 * ---------------------------------------------------------------------------
 * `POST /schedule/authorizations` and `.../revoke` charge `service:manage`, NOT
 * a scheduling key, and the API says why in its own header: putting the ceiling
 * behind `schedule:*` "would say that whoever runs the roster may decide what a
 * payer authorised". This screen is where `service:manage` already lives, and it
 * is where the EXPECTATION (`service_target`) is already rendered — which makes
 * it the one place a reader can see the two side by side and be told, in the
 * product's own words, that they are not the same quantity.
 *
 * The card above this one says "Recommended amounts — recommendations, never
 * authorizations." This is the other half of that sentence, and it had no
 * screen at all: the ceiling could not be created, listed or revoked from
 * anywhere in the product.
 *
 * ---------------------------------------------------------------------------
 * WHAT THE PARITY LEDGER GOT WRONG, AND IT MATTERS FOR THE COPY
 * ---------------------------------------------------------------------------
 * The ledger says the ceiling "is ENFORCED on scheduling — so a scheduler is
 * refused with CeilingExceededError 409 by a limit nobody can see". It is not.
 * `assertWithinCeiling` is exported and called by NOTHING in `apps/api/src`;
 * nothing throws `CeilingExceededError` on any live path, and no schedule write
 * is refused by a ceiling today. So this screen must not promise that setting a
 * ceiling will stop a booking — it would be describing a refusal the product
 * does not perform. What a ceiling DOES do is answer "was this authorised at the
 * time", feed the coverage panel, and raise the `above_authorization_ceiling`
 * finding. The copy below says exactly that and no more.
 *
 * ---------------------------------------------------------------------------
 * REVOKED IS NOT ABSENT
 * ---------------------------------------------------------------------------
 * The list asks for EVERY status. A revoked authorization is the explanation for
 * work that already happened under it, and dropping it would render "revoked"
 * and "never authorized" identically — opposite answers to a payer's question.
 * `authorizationStanding` is the existing predicate that keeps the five apart
 * (in force / starts later / window has passed / revoked / no dates recorded)
 * and is imported rather than re-derived.
 */

/**
 * ONE KEY, BOTH DIRECTIONS. PH-AUTHZ-003: `GET /schedule/authorizations`
 * charged `service:read`, which is seeded to the `worker` role, and `clientId`
 * was optional — so a bare call returned EVERY ceiling in the organization,
 * payer and payer reference included, to every field worker. The API now reads
 * a ceiling on `service:manage`, so this screen asks the same key it always
 * asked to WRITE one.
 */
const CEILING_MANAGE = 'service:manage';
/**
 * THE ORGANIZATION-WIDE QUESTION HAS ITS OWN KEY. With no client chosen the
 * list asks for every client's ceiling, and the API refuses that without
 * `directory:read_all` — the same key and the same rule
 * `GET /directory/assignments` applies to the same shape of question. Asked
 * here so a supervisor sees the picker and the sentence below it, rather than
 * a 403 from a request the screen should never have sent.
 */
const CLIENT_READ_ALL = 'directory:read_all';

const EMPTY_DRAFT = {
  authorizingEntity: '',
  authorizationRef: '',
  maxUnits: '',
  unit: 'hours' as SCH.AuthorizationUnit,
  period: 'week' as SCH.AuthorizationPeriod,
  startsOn: '',
  endsOn: '',
  note: '',
};

export function AuthorizationCeilings() {
  const qc = useQueryClient();
  const permissions = usePermissions();
  const { has } = permissions;
  // ONE NAME, because it is now one key. Reading a ceiling and recording one
  // are both `service:manage` on the API; keeping a separate `canRead` here
  // would be two names for one grant, which is how a screen drifts away from
  // the rule it mirrors.
  const canManageCeilings = has(CEILING_MANAGE);
  const canAskOrgWide = has(CLIENT_READ_ALL);
  const canReadDirectory = has('directory:read');

  const [clientId, setClientId] = useState('');
  /**
   * WHETHER THE QUESTION IS ASKED AT ALL, and the reason it is a named value
   * rather than an inline `enabled:`.
   *
   * The bare list is the organization-wide question the API refuses without
   * `directory:read_all`, so this screen does not send it — the difference
   * between "choose a client" and a 403 the reader has to interpret.
   *
   * BUT A DISABLED QUERY IS NOT AN EMPTY ONE. TanStack reports a disabled query
   * as pending-and-idle, so `isLoading` is false and `data` is undefined, and
   * the list block below would have fallen straight through to `SuccessEmpty`
   * — "No authorizations recorded. No ceiling has been recorded." A supervisor
   * who had simply not chosen a client yet would have been told, in the
   * product's own voice, that nobody in the organization has a ceiling. That is
   * the absence-versus-refusal collapse this codebase names everywhere, and it
   * would have been INTRODUCED by narrowing the read. So the same value that
   * decides whether to ask decides whether to render an answer.
   */
  const asked = canManageCeilings && (clientId !== '' || canAskOrgWide);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [createError, setCreateError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [revokeNote, setRevokeNote] = useState('');
  const [revokeError, setRevokeError] = useState<{ id: string; message: string } | null>(null);

  const clients = useQuery({
    queryKey: ['directory', 'clients'],
    queryFn: () => directory.clients(),
    enabled: canReadDirectory,
  });

  const list = useQuery({
    queryKey: ['schedule', 'authorizations', clientId],
    queryFn: () => schedule.authorizations(clientId === '' ? {} : { clientId }),
    enabled: asked,
  });

  const create = useMutation({
    mutationFn: () =>
      schedule.createAuthorization({
        clientId,
        authorizingEntity: draft.authorizingEntity.trim(),
        authorizationRef:
          draft.authorizationRef.trim() === '' ? null : draft.authorizationRef.trim(),
        period: draft.period,
        maxUnits: Number(draft.maxUnits),
        unit: draft.unit,
        // A BLANK BOUND IS OPEN, NOT UNKNOWN. `ceilingForce` treats null as
        // open-ended — "12 hours a week until we say otherwise" — which is the
        // ordinary case, so an empty field is sent as null rather than as
        // today's date.
        startsOn: draft.startsOn === '' ? null : draft.startsOn,
        endsOn: draft.endsOn === '' ? null : draft.endsOn,
        note: draft.note.trim() === '' ? null : draft.note.trim(),
      }),
    onSuccess: () => {
      setCreating(false);
      setDraft(EMPTY_DRAFT);
      setCreateError(null);
      void qc.invalidateQueries({ queryKey: ['schedule', 'authorizations'] });
      // The coverage panel reads the same ceilings through the service-level
      // reconciliation, so a new one has to reach it too.
      void qc.invalidateQueries({ queryKey: ['coverage', 'service-level'] });
    },
    onError: (err: unknown) => setCreateError(messageOf(err)),
  });

  const revoke = useMutation({
    mutationFn: (id: string) =>
      schedule.revokeAuthorization(id, revokeNote.trim() === '' ? {} : { note: revokeNote.trim() }),
    onSuccess: () => {
      setRevokingId(null);
      setRevokeNote('');
      setRevokeError(null);
      void qc.invalidateQueries({ queryKey: ['schedule', 'authorizations'] });
      void qc.invalidateQueries({ queryKey: ['coverage', 'service-level'] });
    },
    onError: (err: unknown, id: string) => {
      // The confirm stays OPEN on failure, for the reason the schedule screen
      // learned the hard way: on this row the affordance disappearing IS the
      // success signal, so firing it on failure renders a refusal as a success.
      setRevokeError({ id, message: messageOf(err) });
    },
  });

  const directoryGate = authorityGate(permissions, 'directory:read').kind;
  const clientLabel = useMemo(
    () => clientNameResolver(directoryGate, { data: clients.data, isError: clients.isError }),
    [clients.data, clients.isError, directoryGate],
  );

  const clientPicker = pickerState(
    listPhase(clients, clients.data?.length),
    'clients',
    'Every client',
  );

  if (!canManageCeilings) {
    // ABSENT, not a ghost. `service:manage` is what the list charges; a reader
    // without it is not being kept from a ceiling, they are on a screen whose
    // section is not theirs.
    return null;
  }

  const rows = list.data ?? [];
  const today = localDateIn(Intl.DateTimeFormat().resolvedOptions().timeZone, new Date());

  return (
    <SettingsCard>
      <div className="rn-card-head">
        <h2 className="rn-card-title">Authorizations</h2>
        {canManageCeilings ? (
          <button
            type="button"
            className="rn-btn-quiet"
            disabled={clientId === ''}
            onClick={() => {
              setCreating((c) => !c);
              setCreateError(null);
            }}
          >
            {creating ? 'Close' : 'Record an authorization'}
          </button>
        ) : null}
      </div>
      <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
        A CEILING: what a payer says may not be exceeded. This is not the target above it — a target
        is what should be delivered, a ceiling is what may not be passed, and the two are never
        averaged. Recording one does not block a booking today; it answers whether work was
        authorised at the time, and it is what the coverage panel and the “scheduled above the
        authorization” finding both read.
      </p>

      <SettingsField label="Client">
        <select
          value={clientId}
          disabled={clientPicker.disabled}
          onChange={(e) => {
            setClientId(e.target.value);
            setCreating(false);
            setRevokingId(null);
          }}
        >
          <option value="">{clientPicker.placeholder}</option>
          {(clients.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.initials}
            </option>
          ))}
        </select>
        {clientPicker.note !== null ? (
          <p className="rn-muted" style={{ margin: 0 }}>
            {clientPicker.note}
          </p>
        ) : null}
      </SettingsField>

      {/*
        ONE SENTENCE, CHOSEN BY THE PICKER AND NOT BY A PERMISSION. It used to
        be chosen by `canWrite`; making the read half of it turn on
        `!canAskOrgWide` would have been a sentence picked off the FALSE of a
        permission boolean, which also means "the set has not arrived yet" —
        the defect `unknown-is-not-denied.test.ts` keeps at zero.

        WHAT IT NO LONGER SAYS. It used to end "there is no organization-wide
        one", printed above the organization-wide list an org_admin was at that
        moment being shown: `asked` is true with no client chosen whenever
        `canAskOrgWide`, and `list` then calls `schedule.authorizations({})`.
        The sentence is now about RECORDING one, which is true for every reader
        of this card — a ceiling is still created against exactly one person,
        and the button below is still disabled until one is picked — so nothing
        has to decide who sees it and nothing contradicts the rows underneath.
      */}
      {clientId === '' ? (
        <p className="rn-muted" style={{ margin: 0 }}>
          Choose a client to see only their authorizations, and before recording one. A ceiling is
          always recorded against one person.
        </p>
      ) : null}

      {creating && clientId !== '' ? (
        <div className="rn-col" style={{ gap: 'var(--space-8)' }}>
          <SettingsField label="Who authorized it">
            <input
              value={draft.authorizingEntity}
              placeholder="The payer or body that issued it"
              onChange={(e) => setDraft({ ...draft, authorizingEntity: e.target.value })}
            />
          </SettingsField>
          <SettingsField label="Their reference">
            <input
              value={draft.authorizationRef}
              placeholder="Optional — the number on their paperwork"
              onChange={(e) => setDraft({ ...draft, authorizationRef: e.target.value })}
            />
          </SettingsField>
          <SettingsField label="Ceiling">
            <div className="rn-row" style={{ gap: 'var(--space-8)' }}>
              <input
                type="number"
                min={0}
                value={draft.maxUnits}
                onChange={(e) => setDraft({ ...draft, maxUnits: e.target.value })}
              />
              <select
                aria-label="Unit"
                value={draft.unit}
                onChange={(e) =>
                  setDraft({ ...draft, unit: e.target.value as SCH.AuthorizationUnit })
                }
              >
                <option value="hours">hours</option>
                <option value="units">units</option>
                <option value="visits">visits</option>
              </select>
              <span className="rn-muted">per</span>
              <select
                aria-label="Period"
                value={draft.period}
                onChange={(e) =>
                  setDraft({ ...draft, period: e.target.value as SCH.AuthorizationPeriod })
                }
              >
                <option value="day">day</option>
                <option value="week">week</option>
                <option value="month">month</option>
                <option value="quarter">quarter</option>
                <option value="year">year</option>
                <option value="episode">episode</option>
              </select>
            </div>
            {draft.unit !== 'hours' ? (
              <p className="rn-muted" style={{ margin: 0 }}>
                A ceiling in {draft.unit} is recorded but never compared against the schedule.
                Nothing in this product knows how many minutes a payer’s {draft.unit.slice(0, -1)}{' '}
                is, and picking a conversion nobody agreed to would be worse than not comparing.
              </p>
            ) : null}
          </SettingsField>
          <SettingsField label="In force">
            <div className="rn-row" style={{ gap: 'var(--space-8)', alignItems: 'center' }}>
              <input
                aria-label="Starts on"
                type="date"
                value={draft.startsOn}
                onChange={(e) => setDraft({ ...draft, startsOn: e.target.value })}
              />
              <span className="rn-muted">to</span>
              <input
                aria-label="Ends on"
                type="date"
                value={draft.endsOn}
                onChange={(e) => setDraft({ ...draft, endsOn: e.target.value })}
              />
            </div>
            <p className="rn-muted" style={{ margin: 0 }}>
              Leave either blank for open-ended. Blank means OPEN, not unknown — an authorization
              with no end runs until somebody revokes it.
            </p>
          </SettingsField>
          <SettingsField label="Note">
            <input
              value={draft.note}
              onChange={(e) => setDraft({ ...draft, note: e.target.value })}
            />
          </SettingsField>
          {createError !== null ? (
            <p role="status" className="rn-muted" style={{ color: 'var(--color-urgent-deep)' }}>
              {createError} Nothing was recorded.
            </p>
          ) : null}
          <div>
            <button
              type="button"
              className="rn-btn-quiet"
              disabled={
                create.isPending ||
                draft.authorizingEntity.trim() === '' ||
                draft.maxUnits.trim() === '' ||
                Number.isNaN(Number(draft.maxUnits))
              }
              onClick={() => create.mutate()}
            >
              {create.isPending ? 'Recording…' : 'Record it'}
            </button>
          </div>
        </div>
      ) : null}

      {!asked ? null : list.isLoading ? (
        <SkeletonSet surface="table" rows={3} />
      ) : list.isError ? (
        isForbidden(list.error) ? (
          <p className="rn-muted" style={{ margin: 0 }}>
            Authorizations are not shown to you. Do not read that as “no ceiling has been recorded”.
          </p>
        ) : (
          <LoadFailure
            error={list.error}
            message="We couldn’t read the authorizations just now."
            onRetry={() => void list.refetch()}
          />
        )
      ) : rows.length === 0 ? (
        <SuccessEmpty
          heading={clientId === '' ? 'No authorizations recorded' : 'Nothing recorded for them'}
          message="No ceiling has been recorded. Nothing here limits a booking — and nothing here supports billing one either."
        />
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {rows.map((auth) => {
            const standing = authorizationStanding(auth, today);
            const window = dateWindowReading(auth.startsOn, auth.endsOn);
            return (
              <li key={auth.id} className="tc-row" style={{ padding: 'var(--space-8) 0' }}>
                <div
                  className="rn-row"
                  style={{ justifyContent: 'space-between', gap: 'var(--space-8)' }}
                >
                  <span>
                    {auth.authorizingEntity} · {auth.maxUnits} {auth.unit} per {auth.period}
                    {clientId === '' ? (
                      <span className="rn-muted"> · {clientLabel(auth.clientId)}</span>
                    ) : null}
                    {window === null ? null : <span className="rn-muted"> · {window}</span>}
                  </span>
                  <Pill
                    variant={
                      standing === 'in_force'
                        ? 'good'
                        : standing === 'revoked'
                          ? 'urgent'
                          : standing === 'ended'
                            ? 'plain'
                            : 'unsure'
                    }
                    dot
                  >
                    {DATED_STANDING_READING[standing]}
                  </Pill>
                </div>
                {standing === 'revoked' ? (
                  <p className="rn-muted" style={{ margin: 0 }}>
                    Revoked, and kept. This still answers what was authorised while it ran — which
                    is why it is here and not deleted.
                    {auth.note ? ` ${auth.note}` : ''}
                  </p>
                ) : auth.note ? (
                  <p className="rn-muted" style={{ margin: 0 }}>
                    {auth.note}
                  </p>
                ) : null}
                {auth.authorizationRef !== null ? (
                  <p className="rn-muted" style={{ margin: 0 }}>
                    Their reference: {auth.authorizationRef}
                  </p>
                ) : null}
                {canManageCeilings && auth.status === 'active' ? (
                  revokingId === auth.id ? (
                    <div className="rn-row" style={{ gap: 'var(--space-8)', flexWrap: 'wrap' }}>
                      <input
                        aria-label="Why it is being revoked"
                        placeholder="Why — this is kept on the record"
                        value={revokeNote}
                        onChange={(e) => setRevokeNote(e.target.value)}
                      />
                      <button
                        type="button"
                        className="rn-btn-quiet"
                        disabled={revoke.isPending && revoke.variables === auth.id}
                        onClick={() => revoke.mutate(auth.id)}
                      >
                        {revoke.isPending && revoke.variables === auth.id
                          ? 'Revoking…'
                          : revokeError?.id === auth.id
                            ? 'Try again'
                            : 'Confirm revoke'}
                      </button>
                      <button
                        type="button"
                        className="rn-btn-quiet"
                        onClick={() => {
                          setRevokingId(null);
                          setRevokeNote('');
                          setRevokeError(null);
                        }}
                      >
                        Keep it
                      </button>
                      {revokeError?.id === auth.id ? (
                        <span
                          role="status"
                          className="rn-muted"
                          style={{ color: 'var(--color-urgent-deep)' }}
                        >
                          {revokeError.message} This authorization is still in force.
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="rn-btn-quiet"
                      onClick={() => {
                        setRevokingId(auth.id);
                        setRevokeNote('');
                        setRevokeError(null);
                      }}
                    >
                      Revoke
                    </button>
                  )
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </SettingsCard>
  );
}
